import type { Scale } from '@tunasong/schemas'
import type { Chord } from '../chord/index.js'
import { getBorrowedFunction } from './borrowing.js'
import { describeChord } from './descriptions.js'
import { getDiatonicFunction } from './diatonic.js'
import { getSecondaryDominantFunction } from './secondary-dominant.js'
import type { ChordAnalysis } from './types.js'

export interface AnalyzeChordInput {
  chord: Chord
  scale: Scale | undefined
}

/**
 * Analyze a chord against an established scale. Returns structured rationale
 * (function, borrowing, description) or null when no scale is set.
 */
export const analyzeChord = ({ chord, scale }: AnalyzeChordInput): ChordAnalysis | null => {
  if (!scale) {
    return null
  }
  const diatonic = getDiatonicFunction(chord, scale)
  if (diatonic) {
    return {
      chordFunction: diatonic,
      borrowing: null,
      description: describeChord({ chordFunction: diatonic, borrowing: null }),
    }
  }
  const borrowed = getBorrowedFunction(chord, scale)
  if (borrowed) {
    return { ...borrowed, description: describeChord(borrowed) }
  }
  const secondaryDominant = getSecondaryDominantFunction(chord, scale)
  if (secondaryDominant) {
    return { ...secondaryDominant, description: describeChord(secondaryDominant) }
  }
  return {
    chordFunction: 'chromatic',
    borrowing: null,
    description: describeChord({ chordFunction: 'chromatic', borrowing: null }),
  }
}
